import * as React from 'react';
import {
  useReactTable,
  createColumnHelper,
  getCoreRowModel,
  flexRender
} from '@tanstack/react-table';
import { checkIcon, modifierIcon } from '../../../../../assets/icons';
import classes from './SectionsTable.module.scss';
import { FC, useEffect, useRef, useState } from 'react';
import { ActionMenu } from '../../../ActionMenu/ActionMenuGeneral/ActionMenu';
import { handleClickOutside } from '../../../../../utils/Admin/handleClickOutside';
import {
  CategoriesServicesObjectData,
  CategoriesTableType
} from '../../../../../types/Admin/CategoriesTopicsTypes';
import { useActivateDeactivateCategory } from '../../../../../hooks/Admin/CategoriesAndServisesTable/useActivateDeactivateCategory';
import { ModalMode } from '../../../../../types/enums';
import Skeleton from '../../../Skeleton/Skeleton';

const columnSizes = {
  nameRuWidth: 400,
  nameBeWidth: 280,
  nameEnWidth: 280,
  startFinishWorkWidth: 123,
  branchesWidth: 140,
  queuesWidth: 140,
  isActiveWidth: 94
};
type SectionsTableProps = {
  setActiveRowData: (data: CategoriesTableType) => void;
  setModalActive: (value: boolean) => void;
  setModalDeleteActive: (value: boolean) => void;
  tableData: CategoriesServicesObjectData;
  setModalMode: (value: string) => void;
  isLoadingData: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canChangeActivity?: boolean;
};

const { nameRuWidth, nameBeWidth, nameEnWidth, startFinishWorkWidth, branchesWidth, queuesWidth, isActiveWidth } = columnSizes;

const columnHelper = createColumnHelper<CategoriesTableType>();

const SectionsTable: FC<SectionsTableProps> = ({
  setActiveRowData,
  setModalActive,
  tableData,
  setModalDeleteActive,
  setModalMode,
  isLoadingData,
  canEdit = true,
  canDelete = false,
  canChangeActivity = false
}) => {
  const [activeRow, setActiveRow] = React.useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  const { activateOrDeactivateCategory } = useActivateDeactivateCategory(setOpenMenuId);

  const menuElement = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => handleClickOutside(event, menuElement, setOpenMenuId);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [setOpenMenuId]);

  function handleEditClick() {
    setModalActive(true);
    setOpenMenuId(null);
  }

  function handleOpenDeleteModal() {
    setModalDeleteActive(true);
    setOpenMenuId(null);
  }

  function handleActivateDeactivateClick(rowId: number, actionType: string) {
    activateOrDeactivateCategory({ categoryId: rowId, data: { state: actionType } });
  }

  const categoriesColumns = React.useMemo(() => [
    columnHelper.accessor('nameRu', {
      header: () => <div className={classes.headerFirstColumnContent}>Наименование</div>,
      cell: ({ getValue, row }) => (
        <div className={classes.bodyFirstColumnContent}>
          {(canEdit || canDelete || canChangeActivity) && (
            <div className={classes.editMenuContainer}>
              <img
                id={'imgMenu'}
                src={modifierIcon}
                alt="modifierService"
                className={classes.modifierImage}
                onClick={() => {
                  if (openMenuId === row.original.id) {
                    setOpenMenuId(null);
                  } else {
                    setOpenMenuId(row.original.id);
                  }
                  setActiveRowData(row.original);
                  setModalMode(ModalMode.Edit);
                }}
              />
              {openMenuId === row.original.id && (
                <div ref={menuElement} className={classes.actonMenuContainer}>
                  <ActionMenu
                    isActivate={row.original.isActive}
                    editOnClick={canEdit ? handleEditClick : undefined}
                    deleteOnClick={canDelete ? () => handleOpenDeleteModal() : undefined}
                    activateDeactivateClick={
                      canChangeActivity
                        ? () =>
                            handleActivateDeactivateClick(
                              row.original.id,
                              row.original.isActive ? 'Деактивировать' : 'Активировать'
                            )
                        : undefined
                    }
                  />
                </div>
              )}
            </div>
          )}
          <div className={classes.firstColumnText}>{getValue()}</div>
        </div>
      ),
      size: nameRuWidth
    }),
    columnHelper.accessor('nameBe', {
      header: () => <div className={classes.headerContent}>Наименование BE</div>,
      cell: ({ getValue }) => <div className={classes.colunmContent}>{getValue()}</div>,
      size: nameBeWidth
    }),
    columnHelper.accessor('nameEn', {
      header: () => <div className={classes.headerContent}>Наименование EN</div>,
      cell: ({ getValue }) => <div className={classes.colunmContent}>{getValue()}</div>,
      size: nameEnWidth
    }),
    columnHelper.accessor('startFinishWork', {
      header: () => <div className={classes.headerContentCenter}>Начало-конец работы</div>,
      cell: () => <div className={classes.columnContentCenter}>-</div>,
      size: startFinishWorkWidth
    }),
    columnHelper.accessor((row) => row.branches?.map((b) => b.name).join(', ') ?? '', {
      id: 'branches',
      header: () => <div className={classes.headerContent}>Филиалы</div>,
      cell: ({ row }) => (
        <div className={classes.colunmContent} title={row.original.branches?.map((b) => b.name).join(', ')}>
          {row.original.branches?.length ? row.original.branches.map((b) => b.name).join(', ') : '—'}
        </div>
      ),
      size: branchesWidth
    }),
    columnHelper.accessor((row) => row.queues?.map((q) => q.name).join(', ') ?? '', {
      id: 'queues',
      header: () => <div className={classes.headerContent}>Очереди</div>,
      cell: ({ row }) => (
        <div className={classes.colunmContent} title={row.original.queues?.map((q) => q.name).join(', ')}>
          {row.original.queues?.length ? row.original.queues.map((q) => q.name).join(', ') : '—'}
        </div>
      ),
      size: queuesWidth
    }),
    columnHelper.accessor('isActive', {
      header: () => <div className={classes.headerContentCenter}>Активный</div>,
      cell: ({ getValue }) => {
        return getValue() ? (
          <div className={classes.columnContentCenter}>
            <img src={checkIcon as unknown as string} alt="active" />
          </div>
        ) : (
          ''
        );
      },
      size: isActiveWidth
    })
  ], [openMenuId, canEdit, canDelete, canChangeActivity, tableData]);

  const handleClick = (id: string) => {
    setActiveRow(id);
  };

  const columns = categoriesColumns;

  const tableDataFinal = React.useMemo(
    () => (isLoadingData ? Array(5).fill({}) : (tableData?.sections || [])),
    [isLoadingData, tableData]
  );

  const tableColumns = React.useMemo(
    () =>
      isLoadingData
        ? columns.map((column) => ({
            ...column,
            cell: () => <Skeleton />
          }))
        : columns,
    [isLoadingData, columns]
  );

  const table = useReactTable({
    data: tableDataFinal,
    columns: tableColumns,
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    getCoreRowModel: getCoreRowModel()
  });
  return (
    <div className={classes.container}>
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{ width: `${header.getSize()}px` }}>
                      <div className={classes.thTitle}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        <div
                          {...{
                            onDoubleClick: () => header.column.resetSize(),
                            onMouseDown: header.getResizeHandler(),
                            className: `resizer ltr ${header.column.getIsResizing() ? 'isResizing' : ''}`
                          }}>
                          <div className={classes.colorResizer} />
                        </div>
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => handleClick(row.id)}
                className={activeRow === row.id ? classes.activeRow : ''}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SectionsTable;
